import type {SDK, ISO_639_1, Player, LeaderboardDescription} from 'ysdk'
import {LANGS} from 'src/lib/translates/consts'
import type {UserData, EntryType} from './types'

class YandexSDK {
  lbName = 'gallowtop'
  ysdk: SDK | null = null
  player: Player | null = null
  isDebugMode: boolean = false
  lb: LeaderboardDescription | null = null

  constructor () {
    const {searchParams} = new URL(document.location.href)
    this.isDebugMode = Boolean(searchParams.get('debug-mode'))
    this.console('Create instance of YandexSDK')
  }
  
  // @ts-expect-error ts(6133)
  console(...args) {
    if (this.isDebugMode) {
      console.log(...args)
    }
  }

  ready() {
    if (this.ysdk) {
      this.ysdk.features.LoadingAPI?.ready()
      this.console('YandexSDK.ready')
    } else {
      this.console('ready: ysdk is null')
    }
  }

  startGame() {
    if (this.ysdk) {
      this.ysdk.features.GameplayAPI.start()
      this.console('YandexSDK.startGame')
    } else {
      this.console('startGame: ysdk is null')
    }
  }

  endGame() {
    if (this.ysdk) {
      this.ysdk.features.GameplayAPI.stop()
      this.console('YandexSDK.endGame')
    } else {
      this.console('endGame: ysdk is null')
    }
  }

  getLang() {
    this.console('YandexSDK.getLang')
    const sng: Array<ISO_639_1> = ['ru', 'be', 'kk', 'uz']
    const lang = this.ysdk?.environment.i18n.lang
  
    if (!lang) return undefined
  
    if (sng.includes(lang)) return LANGS.RU
    if (lang === 'uk') return LANGS.UK
    if (lang === 'tr') return LANGS.TR

    return LANGS.EN
  }
  
  setSDK(ysdk: SDK) {
    this.console('YandexSDK.setSDK: ', ysdk)
    this.ysdk = ysdk
  }

  async healIosLC() {
    if (this.ysdk) {
      try {
        this.console('YandexSDK: Switch on safeStorage')
        const safeStorage = await this.ysdk.getStorage()
        Object.defineProperty(window, 'localStorage', { get: () => safeStorage })
      } catch(_) {
        this.console('localStorage is not safe')
      }
    } else {
      this.console('healIosLC: ysdk is null')
    }
  }

  async getPlayer() {
    if (this.ysdk) {
      try {
        this.console('YandexSDK.getPlayer')
        this.player = await this.ysdk.getPlayer()
        this.console('YandexSDK.getPlayer: have gotten')
      } catch(_) {
        this.console('Ошибка при инициализации объекта Player')
      }
    } else {
      this.console('getPlayer: ysdk is null')
    }
  }

  async getLeaderBoard() {
    if (this.ysdk) {
      try {
        this.console('YandexSDK.getLeaderBoard')
        this.lb = await this.ysdk.leaderboards.getDescription(this.lbName);
        this.console('YandexSDK.getLeaderBoard: have gotten')
      } catch(_) {
        this.console('Ошибка при инициализации объекта Player')
      }
    } else {
      this.console('getLeaderBoard: ysdk is null')
    }
  }

  async saveUserScore(data: UserData) {
    if (this.ysdk) {
      const score = Object.values(data).reduce((akk, item) => {
        return akk + item.length
      }, 0)
      this.console('YandexSDK.saveUserScore: ', score)
      try {
        await this.ysdk.leaderboards.setScore(this.lbName, score)
      } catch (_) {
        this.console('Ошибка сохранения очков')
      }
    } else {
      this.console('saveUserScore: ysdk is null')
    }
  }

  async saveUserProgress(data: UserData) {
    if (this.player) {
      try {
        this.console('YandexSDK.saveUserProgress')
        await this.player.setData(data)
        await this.saveUserScore(data)
        this.console('YandexSDK.saveUserProgress: have saved')
      } catch (_) {
        this.console('Ошибка сохранения данных')
      }
    } else {
      this.console('saveUserProgress: player is null')
    }
  }

  async getUserProgress(): Promise<UserData | undefined> {
    if (this.player) {
      try {
        this.console('YandexSDK.getUserProgress')
        const data = await this.player.getData()
        this.console('YandexSDK.getUserProgress: ', data)
        return data as UserData
      } catch (_) {
        this.console('Ошибка сохранения данных')
      }
    } else {
      this.console('getUserProgress: player is null')
    }
  }

  async getLeaderBoardEntries(): Promise<EntryType[] | undefined> {
    if (this.ysdk) {
      try {
        const playerId = this.player?.getUniqueID() ?? ''
        const {entries} = await this.ysdk.leaderboards.getEntries(this.lbName, {
          quantityTop: 10,
          includeUser: true,
          quantityAround: 3
        })
        return entries.map<EntryType>((item) => ({
          rank: item.rank,
          name: item.player.publicName,
          score: item.score,
          smallImgSrc: item.player.getAvatarSrc('small'),
          midImgSrc: item.player.getAvatarSrc('medium'),
          isCurrentUser: playerId === item.player.uniqueID,
        }))
      } catch (_) {
        this.console('Ошибка сохранения очков')
      }
    } else {
      this.console('getLeaderBoardEntries: ysdk is null')
    }
  }

  isSDK(): boolean {
    return !!this.ysdk
  }

  rewardedVideo(rewardedCallback: () => void) {
    if (this.ysdk) {
      this.ysdk.adv.showRewardedVideo({callbacks: {
        onOpen: () => {
          this.console('Video ad open.');
        },
        onRewarded: () => {
          this.console('Rewarded!');
          rewardedCallback()
        },
        onClose: () => {
          this.console('Video ad closed.');
        },
        onError: (e) => {
          this.console('Error while open video ad:', e);
        },
      }})
      this.console('YandexSDK.rewardedVideo')
    } else {
      this.console('rewardedVideo: ysdk is null')
    }
  }

  async getFlags() {
    if (this.ysdk) {
      return await this.ysdk.getFlags()
    } else {
      this.console('getFlags: ysdk is null')
    }
  }
 }
 
export const YandexService = new YandexSDK()
